import { Alert, ThemeProvider, createTheme, type Theme } from '@mui/material';
import { deDE as coreDeDE } from '@mui/material/locale';
import { deDE as dataGridDeDE } from '@mui/x-data-grid';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { deDE } from '@mui/x-date-pickers/locales';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import de from 'date-fns/locale/de';
import { useEffect, useState } from 'react';
import './App.css';
import { ChangelogNotifier } from './Changelog/ChangelogNotifier';
import { AlertProvider } from './context/AlertContext';
import { ConfirmationProvider } from './context/ConfirmationContext';
import { LoadingSpinner } from './core/LoadingSpinner';
import { PageRouter } from './core/PageRouter';
import SecurityDriver from './core/SecurityDriver';
import { useFetch } from './hooks/useFetch';
import { IConfig } from './Interfaces/IConfig';

function App() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  });
  const [config, setConfig, wasSuccessfullyConfig] = useFetch<
    IConfig | undefined
  >('/config/public');

  const [theme, setTheme] = useState<Theme | undefined>(undefined);

  useEffect(() => {
    if (!config) return;

    setTheme(
      createTheme(
        {
          palette: {
            primary: {
              main: config.ci_color_primary,
            },
            secondary: {
              main: config.ci_color_secondary,
            },
            text: {
              primary: config.ci_color_text,
            },
          },
        },
        deDE, // x-date-pickers translations
        dataGridDeDE, // x-data-grid translations
        coreDeDE // core translations
      )
    );

    const link = document.createElement('link');

    link.id = 'dynamic-favicon';
    link.rel = 'shortcut icon';
    link.href = `data:image/x-icon;base64,${config.favicon}`;

    const oldLink = document.getElementById('dynamic-favicon');
    if (oldLink) {
      document.head.removeChild(oldLink);
    }
    document.head.appendChild(link);
    document.title = `DiNo - ${config.company_name}`;
  }, [config]);

  if (!wasSuccessfullyConfig) {
    return (
      <Alert severity='warning'>
        Die Lizenz von DiNo konnte nicht überprüft werden!
      </Alert>
    );
  } else if (!config || !theme) {
    return <LoadingSpinner height='100%' />;
  } else {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <AlertProvider>
              <SecurityDriver configObject={config}>
                <ConfirmationProvider>
                  <PageRouter configObject={config} />
                  <ChangelogNotifier />
                </ConfirmationProvider>
              </SecurityDriver>
            </AlertProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </LocalizationProvider>
    );
  }
}

export default App;
